import React, { useEffect, useRef, useState } from 'react';
import type { InputProps } from 'antd';
import CommonJs from '@/utils/commonJs';
import MaskedInput from '../Editor/MaskedInput';

type FormattedDecimalInputProps = {
  value?: string | number | null;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  debounceMs?: number;
  onTextChange?: (text: string) => void;
  onValueChange?: (value: number | null) => void;
  onValueCommit?: (value: number | null) => void;
} & Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'disabled' | 'className' | 'placeholder' | 'style'>;

export function parseDecimalInput(displayValue: string | undefined): string {
  if (!displayValue) return '';
  const cleaned = displayValue.replace(/\s/g, '').replace(/,/g, '');
  const parts = cleaned.split('.');
  if (parts.length === 1) return cleaned;
  const integerPart = parts.shift() ?? '';
  const decimalPart = parts.join('').slice(0, 3);
  return decimalPart ? `${integerPart}.${decimalPart}` : `${integerPart}.`;
}

const toDisplayText = (value?: string | number | null): string => {
  if (value == null || String(value).trim() === '') return '';
  const normalized = parseDecimalInput(String(value));
  const num = parseFloat(normalized);
  if (Number.isNaN(num)) return String(value);
  return CommonJs.formatMoney(num);
};

const FormattedDecimalInput: React.FC<FormattedDecimalInputProps> = ({
  value,
  disabled = false,
  placeholder,
  className,
  style,
  debounceMs = 0,
  onTextChange,
  onValueChange,
  onValueCommit,
  ...restProps
}) => {
  const [text, setText] = useState<string>(() => toDisplayText(value));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) return;
    setText(toDisplayText(value));
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  /* eslint-disable */
  const emitValueChange = (next: number | null) => {
    if (!onValueChange) return;
    if (debounceMs > 0) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onValueChange(next), debounceMs);
      return;
    }
    onValueChange(next);
  };

  return (
    <MaskedInput
      maskType="decimal"
      scale={3}
      value={text}
      disabled={disabled}
      className={className}
      placeholder={placeholder ?? '0.000'}
      {...restProps}
      onChange={(value: string) => {
        const v = String(value ?? '');
        if (v === '') {
          setText('');
          onTextChange?.('');
          emitValueChange(null);
          return;
        }
        if (!/^[\d.,-]+$/.test(v)) return;

        setText(v);
        onTextChange?.(v);

        // Cho phép hiện nút Lưu ngay khi gõ (không chờ blur)
        if (v === '.' || v === '-' || v.endsWith('.')) {
          const partial = parseDecimalInput(v.replace(/\.$/, '').replace(/^-?$/, ''));
          const partialNum = parseFloat(partial);
          if (!Number.isNaN(partialNum)) emitValueChange(partialNum);
          return;
        }

        const normalized = parseDecimalInput(v);
        const num = parseFloat(normalized);
        emitValueChange(Number.isNaN(num) ? null : num);
      }}
      onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = true;
        e.target.select();
      }}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);
        const v = String(e.target?.value ?? '').trim();
        if (v === '') {
          setText('');
          onTextChange?.('');
          queueMicrotask(() => {
            onValueCommit?.(null);
            if (!onValueCommit) onValueChange?.(null);
          });
          return;
        }
        const normalized = parseDecimalInput(v);
        const num = parseFloat(normalized);
        if (!Number.isNaN(num)) {
          const formatted = CommonJs.formatMoney(num);
          setText(formatted);
          onTextChange?.(formatted);
          queueMicrotask(() => {
            if (onValueCommit) onValueCommit(num);
            else onValueChange?.(num);
          });
        } else {
          setText(v);
          onTextChange?.(v);
        }
      }}
      style={{
        width: '100%',
        border: '1px solid #ccc',
        borderRadius: '6px',
        height: '30px',
        ...style,
      }}
    />
  );
};

export default FormattedDecimalInput;
